/* eslint-disable id-length */
/* eslint-disable max-nested-callbacks */
import {
  BATCH_UPLOAD_SUCCESS,
  BATCH_UPLOAD_FAILURE,
  BATCH_UPLOAD_EDIT_LOAD_BATCH,
  BATCH_UPLOAD_EDIT_UPDATE_ROW,
  BATCH_UPLOAD_EDIT_TOGGLE_SKIP_ROW,
  BATCH_UPLOAD_EDIT_SET_LOADING,
  BATCH_UPLOAD_EDIT_SET_LOADING_PROGRESS
} from '@constants/action-types';
import axios from 'axios';
import { runSegmentGeocode } from '@actions/data-detail-actions';
import { downloadCSVFromURL } from '@actions/data-table-actions';
import { BASE_API_URL, getAPIRequestUrl, getDetailAPIRequestUrl } from '@constants/endpoints';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { decodeDetails, getAction, getTemplate, mergeActionWithTemplate, computeMetadataDefaults } from '@utils/data-detail-utils';

const uploadSuccess = payload => ({
  type: BATCH_UPLOAD_SUCCESS,
  payload
});

const uploadError = error => ({
  type: BATCH_UPLOAD_FAILURE,
  payload: error
});

export const uploadBatch = (upload, onProgress, onFail) => {
  const url = getAPIRequestUrl('batch');
  const formData = new FormData();
  formData.append('file', upload.file);
  formData.append('active', upload.active);
  formData.append('status', upload.status);
  if (upload.layerType) {
    formData.append('layerType', upload.layerType);
    formData.append('type', 2);
  } else
    if (upload.entityType) {
      formData.append('entityType', upload.entityType);
      formData.append('type', 3);
    }
  const config = { onUploadProgress: onProgress };
  const request = axios.post(url, formData, config);
  return dispatch =>
    request.then(
      data => dispatch(uploadSuccess(data)),
      error => {
        if (onFail) {
          onFail(error);
        }
        return dispatch(uploadError(error));
      }
    );
};

// Download a CSV template for the specified entity type:
export const downloadTemplate = type => {
  const url = `${BASE_API_URL}/batch/template/${type}/`;
  const filename = `${type}-template.csv`;
  return dispatch => dispatch(downloadCSVFromURL(url, filename, type));
};

export const validateBatch = (file, type, onValidationResults) => {
  const url = `${BASE_API_URL}/batch/validate/${type}/`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  const request = axios.post(url, formData);
  request.then(
    data => {
      onValidationResults(data?.data);
    },
    error => {
      onValidationResults({
        errors: [
          `Validation failed: ${error}`
        ]
      });
    }
  );
};

export const getSchema = (file, type, onError, onSchemaResults) => {
  const url = `${BASE_API_URL}/batch/schema/${type}/`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  const request = axios.post(url, formData);
  request.then(
    data => {
      onSchemaResults(data?.data?.rows);
    },
    error => {
      onError(`Error while retrieving fields: ${error}`);
    }
  );
};

export const convertToJSON = (file, type, fields, onError, onResults) => {
  const url = `${BASE_API_URL}/batch/tojson/${type}/`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fields', fields);
  formData.append('type', type);
  const request = axios.post(url, formData);
  request.then(
    data => {
      onResults(data?.data);
    },
    error => {
      onError(`Error while retrieving fields: ${error}`);
    }
  );
};

export const loadBatch = ({ fileId, rows, fileName, entityType }) => ({
  type: BATCH_UPLOAD_EDIT_LOAD_BATCH,
  payload: { fileId, rows, fileName, entityType }
});

export const updateRow = ({ fileId, rowId, updates, errors, segmentErrors }) => ({
  type: BATCH_UPLOAD_EDIT_UPDATE_ROW,
  payload: { fileId, rowId, updates, errors, segmentErrors }
});

export const toggleSkipRow = ({ fileId, rowId }) => ({
  type: BATCH_UPLOAD_EDIT_TOGGLE_SKIP_ROW,
  payload: { fileId, rowId }
});

export const setBatchLoading = (fileId, loading) => ({
  type: BATCH_UPLOAD_EDIT_SET_LOADING,
  payload: { fileId, loading }
});

export const setBatchLoadingProgress = (fileId, { geocoding, entityFields, total }) => ({
  type: BATCH_UPLOAD_EDIT_SET_LOADING_PROGRESS,
  payload: { fileId, geocoding, entityFields, total }
});

// Run geocoding for address data in the rows uploaded via batch upload.
// Returns a Promise that resolves when all geocoding is complete.
export const geocodeBatchRows = (fileId, rows, entityConfig) => dispatch => {
  return new Promise(resolve => {
    // We needed a way to wait for window.google.map.xxxx data to appear,
    // since sometimes this code might run before the Google code is
    // available on the javascript window.xxxx namespace.
    const waitForGoogleMaps = callback => {
      const poll = () => {
        if (window.mapSetup) {
          callback();
        } else {
          setTimeout(poll, 100);  // Check every 100ms
        }
      };
      poll();
    };

    // Run the geocoding of rows in batches, with a delay (our backend can handle it
    // but I added this since I don't know if Google might rate limit us, if we
    // call geocodeByAddress() too often).
    const processGeocodingQueue = (rowsToProcess, batchSize = 10, delay = 1000) => {
      let processedCount = 0;
      const totalRows = rowsToProcess.length;

      if (totalRows === 0) {
        resolve();
        return;
      }

      let i = 0;
      const processBatch = () => {
        const batch = rowsToProcess.slice(i, i + batchSize);
        if (batch.length === 0) {
          return;  // All done
        }

        const batchPromises = batch.map(row => {
          const segments = row.data.segments || [];
          const segmentPromises = segments.map(segment => {
            if (segment.shape) {
              return Promise.resolve({ segment, error: null });
            }

            // Special case for Soundtransit:
            if (entityConfig?.use_layer_geocoding) {
              const params = {
                on_street: segment.on_street,
                from_address: segment.from_address,
                from_street: segment.from_street,
                to_address: segment.to_address,
                to_street: segment.to_street,
                from_station: segment.from_station,
                to_station: segment.to_station,
                line_name: segment.line_name
              };

              return axios.get(`${BASE_API_URL}/street_segment/`, { params })
                .then(response => {
                  const results = response.data;
                  if (results.length > 0) {
                    const {feature, ...rest} = results[0];
                    return {
                      segment: {
                        ...segment,
                        ...rest,
                        shape: feature.geometry
                      },
                      error: null
                    };
                  }
                  return { segment, error: null };
                })
                .catch(error => {
                  return {
                    segment,
                    error: {
                      shape: error?.response?.data?.error
                    }
                  };
                });
            }

            const { display_from, display_to } = segment;

            const fromPromise = display_from ? geocodeByAddress(display_from).then(results => getLatLng(results[0]))
              .catch(() => null) : Promise.resolve(null);
            const toPromise = display_to ? geocodeByAddress(display_to).then(results => getLatLng(results[0]))
              .catch(() => null) : Promise.resolve(null);

            return Promise.all([fromPromise, toPromise]).then(([start, end]) => {
              // At least a start point is needed:
              if (start) {
                return runSegmentGeocode(start, end)
                  .then(segmentWithShape => ({
                    segment: { ...segment, ...segmentWithShape },
                    error: null
                  }))
                  .catch(() => ({ segment, error: null }));
              }
              return { segment, error: null };
            })
              .catch(() => ({ segment, error: null }));
          });

          return Promise.all(segmentPromises).then(updatedSegments => {
            const segmentsObj = updatedSegments.map(item => {
              const updatedSegment = item.segment;

              // If we specified from_station or to_station for geocoding,
              // but got back only coordinates without address info,
              // set the title to the station name
              if ((updatedSegment.from_station || updatedSegment.to_station) &&
                  (!updatedSegment.on_street && !updatedSegment.from_address &&
                   !updatedSegment.from_street && !updatedSegment.to_address &&
                   !updatedSegment.to_street)) {
                const stationName = updatedSegment.from_station || updatedSegment.to_station;
                return {
                  ...updatedSegment,
                  title: stationName
                };
              }

              return updatedSegment;
            });
            const errors = updatedSegments.map(item => item.error);
            const hasActualErrors = errors.some(error => error !== null);
            const finalErrors = hasActualErrors ? errors : [];

            const updatedData = {
              // Only update segments, since row.data might have changed due to processing.
              segments: segmentsObj
            };
            dispatch(updateRow({
              fileId,
              rowId: row.id,
              updates: updatedData,
              // eslint-disable-next-line no-undefined
              segmentErrors: finalErrors.length > 0 ? finalErrors : undefined
            }));

            processedCount += 1;
            dispatch(setBatchLoadingProgress(fileId, { geocoding: processedCount }));

            return row;
          });
        });

        Promise.all(batchPromises).then(() => {
          i += batchSize;
          if (i < rowsToProcess.length) {
            setTimeout(processBatch, delay);
          } else {
            // All batches processed
            resolve();
          }
        });
      };

      processBatch();
    };

    // Launch the geocoding:
    waitForGoogleMaps(() => {
      const rowsToGeocode = rows.filter(row => row.data.segments?.some(segment => !segment.shape));
      if (rowsToGeocode.length === 0) {
        resolve();
        return;
      }
      processGeocodingQueue(rowsToGeocode);
    });
  });
};

// Fetch missing entity fields from the backend for batch upload edit.
// This retrieves the full entity data for records that exist and merges
// the CSV data with the existing data, so users see complete records.
export const fetchMissingEntityFields = (fileId, rows, entityType, categoryTypes) => (dispatch, getState) => {
  return new Promise(resolve => {
    // Get metadata from OPTIONS call (stored by fetchDataDetailOptions)
    const state = getState();
    const options = state.dataDetail?.options?.[entityType];
    let metadata = null;
    if (options) {
      let { action } = getAction(options);
      const template = getTemplate(entityType);
      if (template) {
        action = computeMetadataDefaults(
          mergeActionWithTemplate(action, template),
          state.dataTypes
        );
      }
      metadata = action;
    }
    // Filter rows that have an ID or external_id (existing entities to update)
    const rowsToFetch = rows.filter(row => row.data.id || row.data.external_id);

    if (rowsToFetch.length === 0) {
      resolve();
      return;
    }

    let processedCount = 0;
    const totalRows = rowsToFetch.length;
    const batchSize = 10;
    const delay = 500;

    // Fields that should not be overwritten with empty strings from CSV
    // (these are identifiers that should come from backend if CSV doesn't have them)
    const protectedFields = ['id'];

    // Convert backend's category_dict to frontend's |categoryName format
    const convertCategories = backendData => {
      const categoryData = {};
      if (backendData.category_dict && categoryTypes) {
        // Group category_dict entries by their category type name
        Object.values(categoryTypes).forEach(categoryType => {
          const categoryName = categoryType.name;
          const categoryEntries = backendData.category_dict.filter(
            cat => cat.name === categoryName
          );

          if (categoryEntries.length > 0) {
            if (categoryType.is_multiple) {
              // Multi-select: array of IDs
              categoryData[`|${categoryName}`] = categoryEntries.map(entry => entry.id);
            } else {
              // Single-select: single ID
              categoryData[`|${categoryName}`] = categoryEntries[0].id;
            }
          } else {
            // No category value for this type
            categoryData[`|${categoryName}`] = null;
          }
        });
      }
      return categoryData;
    };

    // Backend-only/read-only fields that should NOT be included in CSV output.
    // These are generated by the API and cannot be set by the user.
    const excludedBackendFields = [
      'created', 'modified', 'active', 'created_by', 'modified_by',
      'overlaps',
      'agency_type', 'agency_name', 'agency_type_name',
      'color',
      'conflict_status',
      'type_name', 'type_display_name',
      'owner_list', 'owner_dict',
      'undefined_streets', 'polygons',
      'datetime_parts',
      'group_ids',
      'generates_overlaps',
      'checksum',
      'category_dict'  // This gets converted to |categoryName format, so exclude the original
    ];

    // Helper function to merge backend data with CSV data
    const mergeEntityData = (row, backendData) => {
      // Decode backend data using metadata (handles dates, datetimes, etc.)
      // This is the same processing done on the normal entity edit page
      let decodedBackendData;
      if (metadata) {
        decodedBackendData = decodeDetails(backendData, metadata);
      } else {
        // Fallback if no metadata available
        decodedBackendData = { ...backendData };
        // Convert and add categories from backend
        const backendCategories = convertCategories(backendData);
        Object.assign(decodedBackendData, backendCategories);
        // Remove category_dict since we've converted it to |categoryName format
        delete decodedBackendData.category_dict;
      }

      // Filter out backend-only fields that shouldn't be in CSV
      const filteredBackendData = {};
      Object.keys(decodedBackendData).forEach(key => {
        if (!excludedBackendFields.includes(key)) {
          filteredBackendData[key] = decodedBackendData[key];
        }
      });

      // Start with filtered backend data as base
      const mergedData = { ...filteredBackendData };

      // Overwrite with CSV data (CSV data has priority)
      Object.keys(row.data).forEach(key => {
        const csvValue = row.data[key];

        // Skip protected fields if CSV value is empty/falsy
        // (we want to keep the backend's ID, not overwrite with empty string)
        if (protectedFields.includes(key)) {
          if (csvValue !== null && csvValue !== void 0 && csvValue !== '') { // eslint-disable-line no-void
            mergedData[key] = csvValue;
          }
          return;
        }

        // For category fields (starting with |), only overwrite if CSV has a value
        if (key.startsWith('|')) {
          if (csvValue !== null && csvValue !== void 0) { // eslint-disable-line no-void
            mergedData[key] = csvValue;
          }
          return;
        }

        // For other fields, use CSV value if it's not null/undefined
        if (csvValue !== null && csvValue !== void 0) { // eslint-disable-line no-void
          mergedData[key] = csvValue;
        }
      });

      // Special handling for segments: CSV has address data, backend has shapes
      if (row.data.segments && row.data.segments.length > 0) {
        // Use segments from CSV but merge with backend segment data for shapes
        const csvSegments = row.data.segments;
        const backendSegments = backendData.segments || [];

        mergedData.segments = csvSegments.map((csvSeg, idx) => {
          // If there's a corresponding backend segment, merge shape if CSV doesn't have one
          if (idx < backendSegments.length && !csvSeg.shape) {
            return {
              ...csvSeg,
              // Keep backend shape if CSV doesn't have one (geocoding will override if needed)
              shape: backendSegments[idx].shape
            };
          }
          return csvSeg;
        });
      } else if (backendData.segments) {
        // If CSV has no segments at all, use backend segments
        mergedData.segments = backendData.segments;
      }

      return mergedData;
    };

    // Helper function to fetch entity by ID
    const fetchById = entityId => {
      const url = getDetailAPIRequestUrl(entityType, entityId);
      return axios.get(url);
    };

    // Helper function to fetch entity by external_id
    const fetchByExternalId = externalId => {
      // Use the attrs filter to search by external_id
      const url = `${BASE_API_URL}/map/?type=${entityType}&attrs=external_id|${encodeURIComponent(externalId)}`;
      return axios.get(url).then(response => {
        const results = response.data.results || response.data;
        if (Array.isArray(results) && results.length > 0) {
          // Return the first matching entity
          return { data: results[0] };
        }
        // No entity found with this external_id
        return Promise.reject({ response: { status: 404 } });
      });
    };

    const processBatch = startIndex => {
      const batch = rowsToFetch.slice(startIndex, startIndex + batchSize);
      if (batch.length === 0) {
        resolve();
        return;
      }

      const batchPromises = batch.map(row => {
        const entityId = row.data.id;
        const externalId = row.data.external_id;

        // Determine how to fetch: by ID first, then by external_id
        let fetchPromise;
        if (entityId) {
          fetchPromise = fetchById(entityId);
        } else if (externalId) {
          fetchPromise = fetchByExternalId(externalId);
        } else {
          // Should not happen due to filter above, but handle gracefully
          return Promise.resolve({ success: false, rowId: row.id });
        }

        return fetchPromise
          .then(response => {
            const backendData = response.data;
            const mergedData = mergeEntityData(row, backendData);

            // Update the row with merged data
            dispatch(updateRow({
              fileId,
              rowId: row.id,
              updates: mergedData
            }));

            processedCount += 1;
            dispatch(setBatchLoadingProgress(fileId, { entityFields: processedCount }));

            return { success: true, rowId: row.id };
          })
          .catch(error => {
            // If entity not found (404), continue with CSV data only
            // For other errors, log but continue
            processedCount += 1;
            dispatch(setBatchLoadingProgress(fileId, { entityFields: processedCount }));

            if (error.response?.status !== 404) {
              // eslint-disable-next-line no-console
              console.error(`Error fetching entity (id: ${entityId}, external_id: ${externalId}):`, error);
            }
            return { success: false, rowId: row.id, error };
          });
      });

      Promise.all(batchPromises).then(() => {
        const nextIndex = startIndex + batchSize;
        if (nextIndex < totalRows) {
          setTimeout(() => processBatch(nextIndex), delay);
        } else {
          resolve();
        }
      });
    };

    processBatch(0);
  });
};
